import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { Link } from "react-router-dom";
import { AlertTriangle, TrendingDown, Package, CalendarClock, Target as TargetIcon, ChevronRight, Download } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  purchased_not_displayed: { icon: Package, label: "Bought but not displayed", tone: "high" },
  displayed_not_purchased: { icon: Package, label: "Displayed but no recent order", tone: "medium" },
  never_purchased: { icon: Package, label: "Never purchased", tone: "low" },
  declining_vs_ly: { icon: TrendingDown, label: "YTD declining vs last year", tone: "high" },
  no_recent_order: { icon: CalendarClock, label: "No recent order", tone: "medium" },
  below_target: { icon: TargetIcon, label: "Agent below target", tone: "high" },
};

export default function SalesOpportunities() {
  const [items, setItems] = useState([]);
  const [days, setDays] = useState(60);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/sales-opportunities", { params: { days } }).then(r => setItems(r.data)).finally(() => setLoading(false));
  }, [days]);

  const filtered = filter ? items.filter(i => i.type === filter) : items;
  const grouped = TYPE_META && Object.keys(TYPE_META).reduce((acc, t) => { acc[t] = items.filter(i => i.type === t).length; return acc; }, {});

  const doExport = async (fmt) => {
    try {
      const res = await api.get(`/reports/sales_opportunities`, { params: { fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `MLILY-Sales-Opportunities.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-4" data-testid="opportunities-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight">Sales Opportunities</h1>
          <p className="text-slate-600 mt-1">Auto-detected gaps and follow-ups from sales, floor-display & target data</p>
        </div>
        <div className="flex gap-2 items-center">
          <select value={days} onChange={e => setDays(Number(e.target.value))} className="h-11 px-3 border rounded-lg text-sm" data-testid="opp-days-filter">
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
          </select>
          <button onClick={() => doExport("xlsx")} className="h-11 px-4 border border-slate-300 rounded-lg font-semibold text-sm flex items-center gap-2 hover:border-[#003DA5]" data-testid="export-opps-xlsx"><Download className="w-4 h-4" /> Excel</button>
          <button onClick={() => doExport("pdf")} className="h-11 px-4 border border-slate-300 rounded-lg font-semibold text-sm flex items-center gap-2 hover:border-[#003DA5]" data-testid="export-opps-pdf"><Download className="w-4 h-4" /> PDF</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <button onClick={() => setFilter("")} className={`p-3 rounded-lg border text-left ${filter === "" ? "border-[#003DA5] bg-[#F3F7FC]" : "border-slate-200 bg-white"}`} data-testid="filter-all">
          <div className="text-[10px] uppercase font-semibold text-slate-500">All</div>
          <div className="text-2xl font-heading font-bold text-[#001F54]">{items.length}</div>
        </button>
        {Object.entries(TYPE_META).map(([t, m]) => {
          const Icon = m.icon;
          return (
            <button key={t} onClick={() => setFilter(t)} className={`p-3 rounded-lg border text-left ${filter === t ? "border-[#003DA5] bg-[#F3F7FC]" : "border-slate-200 bg-white"}`} data-testid={`filter-${t}`}>
              <div className="text-[10px] uppercase font-semibold text-slate-500 flex items-center gap-1"><Icon className="w-3 h-3" />{m.label}</div>
              <div className="text-2xl font-heading font-bold text-[#001F54]">{grouped[t] || 0}</div>
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 divide-y">
        {loading && <div className="p-8 text-center text-slate-500">Loading opportunities…</div>}
        {!loading && filtered.length === 0 && <div className="p-8 text-center text-slate-500">No opportunities in this window. 🎯</div>}
        {filtered.slice(0, 200).map((o, i) => {
          const m = TYPE_META[o.type] || { icon: AlertTriangle, tone: "low", label: o.type };
          const Icon = m.icon;
          const tone = o.severity || m.tone;
          const c = tone === "high" ? "bg-red-50 border-l-red-500 text-red-900" : tone === "medium" ? "bg-amber-50 border-l-amber-500 text-amber-900" : "bg-slate-50 border-l-slate-400 text-slate-800";
          return (
            <div key={i} className={`p-4 border-l-4 flex items-start gap-3 ${c}`} data-testid={`opp-item-${i}`}>
              <Icon className="w-5 h-5 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase font-bold tracking-wider opacity-70">{m.label}</div>
                <div className="text-sm font-medium mt-0.5">
                  {o.customer_id && o.customer_name ? (
                    <><Link to={`/accounts/${o.customer_id}`} className="underline hover:no-underline font-semibold" data-testid={`opp-store-link-${i}`}>{o.customer_name}</Link>{o.message.replace(o.customer_name, "")}</>
                  ) : o.message}
                </div>
                {o.value_gap && <div className="text-xs mt-1 opacity-80">Gap: {money(o.value_gap)}</div>}
              </div>
              {o.customer_id && <Link to={`/accounts/${o.customer_id}`} className="text-xs font-semibold inline-flex items-center gap-1 shrink-0" data-testid={`opp-view-${i}`}>View <ChevronRight className="w-3 h-3" /></Link>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
